import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useBlocker } from "react-router-dom";

const NavigationGuardContext = createContext(null);
const EMPTY_GUARD = { dirty: false, busy: false };

export const NavigationGuardProvider = ({ children }) => {
  const guard = useRef(EMPTY_GUARD);
  const shouldBlock = useCallback(({ currentLocation, nextLocation }) => (
    (guard.current.dirty || guard.current.busy) && currentLocation.pathname !== nextLocation.pathname
  ), []);
  const blocker = useBlocker(shouldBlock);
  const actions = useMemo(() => ({
    setGuard: (value) => { guard.current = value; },
    clearGuard: () => { guard.current = EMPTY_GUARD; },
    confirmLeave: () => !guard.current.busy && (!guard.current.dirty || window.confirm(
      "You have unsaved changes. Leave this application without saving?"
    )),
  }), []);

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (actions.confirmLeave()) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker, actions]);

  useEffect(() => {
    const beforeUnload = (event) => {
      if (guard.current.dirty || guard.current.busy) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, []);

  return <NavigationGuardContext.Provider value={actions}>{children}</NavigationGuardContext.Provider>;
};

export const useNavigationGuard = () => useContext(NavigationGuardContext);

export const useUnsavedChanges = (dirty, busy) => {
  const { setGuard, clearGuard } = useNavigationGuard();
  useEffect(() => {
    setGuard({ dirty, busy });
    return clearGuard;
  }, [dirty, busy, setGuard, clearGuard]);
};
